import { Box, Button, Divider, Fade, Paper, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React from 'react'
import { useDispatch } from 'react-redux'
import { WaterSystemsActions, WaterSystemType } from '../../../store/waterSystems'
import { IWaterSystemWCoordinates, IWellWaterSystemWCoordinates } from '../../../types'
import { fNumber } from '../../../utils/formatNumber'
import { ConnectionIcon } from '../../icons/ConnectionIcon'
import { ExpandIcon } from '../../icons/ExpandIcon'
import { InfoIcon } from '../../icons/InfoIcon'
import { PeopleIcon } from '../../icons/PeopleIcon'
import { ViolationIcon } from '../../icons/ViolationIcon'
import { UtilityDetailActions } from '../../../store/utilityDetails'

interface MarkerDetailsPopupProps {
  waterSystemType: WaterSystemType
  details: IWaterSystemWCoordinates | IWellWaterSystemWCoordinates
  position: { x: number; y: number }
  fillColor?: string
  open?: boolean
  handleMouseEnter?: (event: React.MouseEvent<HTMLInputElement>) => void
  handleMouseLeave?: (event: React.MouseEvent<HTMLInputElement>) => void
  staticPosition?: boolean
  showExpand?: boolean
}

const getDisplayData = (
  waterSystemType: WaterSystemType,
  details: IWaterSystemWCoordinates | IWellWaterSystemWCoordinates,
) => {
  if (waterSystemType === WaterSystemType.WELL) {
    const wellDetails = details as IWellWaterSystemWCoordinates
    return {
      address: `Block Group ${wellDetails.GEOID_BlkGrp}`,
      city: wellDetails.State,
      connections: Math.max(1, wellDetails.Est_Wells_2020),
      population: wellDetails.HU_2020,
      violations: wellDetails.Prob_Pub * 100,
    }
  }

  const systemDetails = details as IWaterSystemWCoordinates
  return {
    pwsName: systemDetails.PWS_NAME || 'PWS Name not available',
    address: systemDetails.ADDRESS_LINE1 || systemDetails.ADDRESS_LINE2 || 'Address not available',
    city: systemDetails.CITY_NAME || 'City not available',
    connections: systemDetails.SERVICE_CONNECTIONS_COUNT || 0,
    population: systemDetails.POPULATION_SERVED_COUNT || 0,
    violations: systemDetails.VIOLATIONS_COUNT || 0,
  }
}

const MarkerDetailsPopup: React.FC<MarkerDetailsPopupProps> = ({
  waterSystemType,
  details,
  position,
  fillColor,
  staticPosition = false,
  open = false,
  showExpand = false,
  handleMouseEnter = () => {},
  handleMouseLeave = () => {},
}) => {
  const theme = useTheme()
  const displayData = getDisplayData(waterSystemType, details)
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const openSlideOut = (waterSystem: any) => {

    dispatch(UtilityDetailActions.toggleDrawer())
    dispatch(WaterSystemsActions.setSelectedWaterSystem(waterSystem.PWSID))
  }
  return (
    <Fade in={open} unmountOnExit>
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: staticPosition ? 'relative' : 'absolute',
          flex: staticPosition ? 1 : 'unset',
          left: isMobile ? '50%' : position.x + 'px' ,
          transform: isMobile ? 'translateX(-50%)' : '',
          top: isMobile ? 0 : `${position.y}px`,
          width: '19.25rem',
          borderTop: `4px solid ${fillColor}`,
          borderRadius: '0.5rem',
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[3],
          zIndex: theme.zIndex.drawer + 3,
        }}
      >
        <Paper elevation={3} sx={{ padding: { xs: '0.5rem', sm: '0.8rem' } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'nowrap',
              mb: { xs: 1, sm: 0 },
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '12px', sm: '14px' },
                fontWeight: 500,
                flexGrow: 1,
                marginRight: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {displayData.pwsName?.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
            </Typography>

            {showExpand && (
              <Button
                size="small"
                onClick={openSlideOut}
                startIcon={<ExpandIcon />}
              >
                Expand
              </Button>
            )}
            {!showExpand && <InfoIcon sx={{ fontSize: { xs: 18, sm: 24 }, flexShrink: 0 }} />}
          </Box>

          <Typography
            sx={{
              fontSize: { xs: '10px', sm: '12px' },
              color: theme.palette.grey[600],
              mb: { xs: 1, sm: 0 },
            }}
          >
            {displayData.address}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: { xs: 0.5, sm: 1 },
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
                justifyContent: 'center',
                mb: { xs: 1, sm: 0 },
              }}
            >
              <ConnectionIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              <Typography
                sx={{
                  ml: 0.5,
                  fontWeight: 600,
                  color: theme.palette.primary.dark,
                  fontSize: { xs: '12px', sm: '14px' },
                }}
              >
                {fNumber(displayData.connections)}
              </Typography>
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                marginX: { xs: 0.5, sm: 1 },
                display: { xs: 'none', sm: 'flex' },
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
                justifyContent: 'center',
                mb: { xs: 1, sm: 0 },
              }}
            >
              <PeopleIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              <Typography
                sx={{
                  ml: 0.5,
                  fontWeight: 600,
                  color: theme.palette.primary.dark,
                  fontSize: { xs: '12px', sm: '14px' },
                }}
              >
                {fNumber(displayData.population)}
              </Typography>
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                marginX: { xs: 0.5, sm: 1 },
                display: { xs: 'none', sm: 'flex' },
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
                justifyContent: 'center',
                mb: { xs: 1, sm: 0 },
              }}
            >
              <ViolationIcon sx={{ fontSize: { xs: 20, sm: 24 } }} fillColor={fillColor} />
              <Typography
                sx={{
                  ml: 0.5,
                  fontWeight: 600,
                  color: fillColor,
                  fontSize: { xs: '12px', sm: '14px' },
                }}
              >
                {fNumber(displayData.violations)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Fade>
  )
}

export default MarkerDetailsPopup
